import { Link } from 'react-router-dom';
import { MapPin, Mail, ExternalLink } from 'lucide-react';
import { contactInfo, siteConfig } from '@data/siteData';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      className="bg-slate-950 border-t border-slate-800"
      role="contentinfo"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center" aria-hidden="true">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <h3 className="text-white font-semibold">{siteConfig.name}</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Die Alte Sternwarte Mannheim gehört zu den ältesten Gebäuden der Stadt und ist für
              Baden-Württemberg einzigartig.
            </p>
          </div>

          {/* Contact */}
          <nav aria-labelledby="footer-contact-heading">
            <h4 id="footer-contact-heading" className="text-white font-semibold mb-4">Kontakt</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-slate-400 text-sm">
                <MapPin size={16} className="mt-0.5 shrink-0 text-blue-500" aria-hidden="true" />
                <span>
                  {contactInfo.address.street}
                  <br />
                  {contactInfo.address.postalCode} {contactInfo.address.city}
                </span>
              </li>
              <li>
                <a
                  href={`mailto:${contactInfo.email.general}`}
                  className="flex items-center gap-2 text-slate-400 text-sm hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded"
                >
                  <Mail size={16} className="shrink-0 text-blue-500" aria-hidden="true" />
                  {contactInfo.email.general}
                </a>
              </li>
            </ul>
          </nav>

          {/* Quick Links */}
          <nav aria-labelledby="footer-links-heading">
            <h4 id="footer-links-heading" className="text-white font-semibold mb-4">Schnelllinks</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/termine"
                  className="text-slate-400 text-sm hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded"
                >
                  Termine & Events
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-slate-400 text-sm hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded"
                >
                  Geschichte
                </Link>
              </li>
              <li>
                <Link
                  to="/aktionsbuendnis"
                  className="text-slate-400 text-sm hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded"
                >
                  Aktionsbündnis
                </Link>
              </li>
              <li>
                <Link
                  to="/unterstuetzen"
                  className="text-slate-400 text-sm hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded"
                >
                  Unterstützen
                </Link>
              </li>
            </ul>
          </nav>

          {/* Partners */}
          <nav aria-labelledby="footer-partners-heading">
            <h4 id="footer-partners-heading" className="text-white font-semibold mb-4">Partner</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://www.rem-mannheim.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-slate-400 text-sm hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded"
                >
                  Reiss-Engelhorn-Museen
                  <ExternalLink size={12} aria-hidden="true" />
                  <span className="sr-only">(öffnet in neuem Tab)</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.marchivum.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-slate-400 text-sm hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded"
                >
                  MARCHIVUM
                  <ExternalLink size={12} aria-hidden="true" />
                  <span className="sr-only">(öffnet in neuem Tab)</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.lesezeichen-mannheim.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-slate-400 text-sm hover:text-blue-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded"
                >
                  LeseZeichen Mannheim
                  <ExternalLink size={12} aria-hidden="true" />
                  <span className="sr-only">(öffnet in neuem Tab)</span>
                </a>
              </li>
            </ul>
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} {siteConfig.organization}. Alle Rechte vorbehalten.
          </p>
          <nav aria-label="Rechtliche Links">
            <div className="flex items-center gap-6">
              <Link
                to="/impressum"
                className="text-slate-500 text-sm hover:text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded px-1"
              >
                Impressum
              </Link>
              <Link
                to="/datenschutz"
                className="text-slate-500 text-sm hover:text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded px-1"
              >
                Datenschutz
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </footer>
  );
}
